import { useMemo, useState } from 'react';
import { Modal, Button } from '../primitives';
import { useStavby } from '../../api/hooks';
import type { Stavba } from '../../api/schemas';
import styles from '../ares/CompanyPickerButton.module.css';

/**
 * Tlačítko „🏗 Stavby" — otevře modal se seznamem staveb z DB (/api/stavby/).
 * Po výběru volá `onPick(stavba)`. Analogie k CompanyPickerButton.
 */

export interface StavbaPickResult {
  id: number;
  nazev: string;
  adresa: string;
  stavebni_povoleni: string;
  zakazka_cislo: string;
  datum_zahajeni: string;
  datum_ukonceni: string;
}

export interface StavbaPickerButtonProps {
  onPick: (data: StavbaPickResult) => void;
  label?: string;
  compact?: boolean;
  title?: string;
  disabled?: boolean;
}

function toPickResult(s: Stavba): StavbaPickResult {
  return {
    id: s.id,
    nazev: s.nazev ?? '',
    adresa: s.adresa ?? '',
    stavebni_povoleni: s.stavebni_povoleni ?? '',
    zakazka_cislo: s.zakazka_cislo ?? '',
    datum_zahajeni: s.datum_zahajeni ?? '',
    datum_ukonceni: s.datum_ukonceni ?? '',
  };
}

export function StavbaPickerButton({
  onPick,
  label = 'Stavby',
  compact,
  title,
  disabled,
}: StavbaPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { data: stavby = [], isLoading, error } = useStavby();

  const filtered = useMemo(() => {
    const query = q.trim().toLocaleLowerCase('cs');
    const list = [...stavby].sort((a, b) =>
      (a.nazev ?? '').localeCompare(b.nazev ?? '', 'cs'),
    );
    if (!query) return list;
    return list.filter((s) => {
      const hay = [s.nazev, s.adresa, s.stavebni_povoleni, s.zakazka_cislo]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('cs');
      return hay.includes(query);
    });
  }, [stavby, q]);

  function choose(s: Stavba) {
    onPick(toPickResult(s));
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={title ?? 'Načíst stavbu z adresáře'}
      >
        <span aria-hidden>🏗</span>
        {!compact && <span>{label}</span>}
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        title={title ?? 'Vyberte stavbu'}
        footer={
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Zavřít
          </Button>
        }
      >
        <input
          className={styles.search}
          placeholder="Hledat podle názvu, adresy, zakázky…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />

        {isLoading ? (
          <div className={styles.empty}>Načítám…</div>
        ) : error ? (
          <div className={styles.statusErr}>
            Chyba: {error instanceof Error ? error.message : 'nelze načíst'}
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            {stavby.length === 0
              ? 'Žádné stavby. Stavbu můžete přidat přes „💾 Uložit stavbu".'
              : `Žádný výsledek pro „${q.trim()}".`}
          </div>
        ) : (
          <div className={styles.list}>
            {filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                className={styles.item}
                onClick={() => choose(s)}
              >
                <div className={styles.itemMain}>
                  <div className={styles.itemName}>{s.nazev || '— bez názvu —'}</div>
                  <div className={styles.itemMeta}>
                    {s.zakazka_cislo && (
                      <span className={styles.mono}>zakázka {s.zakazka_cislo}</span>
                    )}
                    {s.stavebni_povoleni && (
                      <span className={styles.mono}>povolení {s.stavebni_povoleni}</span>
                    )}
                  </div>
                  {s.adresa && <div className={styles.itemContact}>{s.adresa}</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
